import React, { useEffect, useRef, useState } from "react";

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileRenderOptions = {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  size?: string;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export interface TurnstileWidgetProps {
  siteKey: string;
  onSuccess?: (token: string) => void;
  onError?: () => void;
  onExpired?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

const TurnstileWidget = ({
  siteKey,
  onSuccess,
  onError,
  onExpired,
  onLoadingChange,
}: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbacksRef = useRef({
    onSuccess,
    onError,
    onExpired,
    onLoadingChange,
  });
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    callbacksRef.current = { onSuccess, onError, onExpired, onLoadingChange };
  }, [onSuccess, onError, onExpired, onLoadingChange]);

  useEffect(() => {
    if (!siteKey) {
      return undefined;
    }

    let cancelled = false;

    const renderWidget = async () => {
      callbacksRef.current.onLoadingChange?.(true);
      try {
        await loadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        if (widgetIdRef.current !== null) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          size: "flexible",
          callback: (token) => {
            callbacksRef.current.onSuccess?.(token);
          },
          "error-callback": () => {
            callbacksRef.current.onError?.();
          },
          "expired-callback": () => {
            callbacksRef.current.onExpired?.();
          },
        });
      } catch {
        setScriptError(true);
        callbacksRef.current.onError?.();
      } finally {
        if (!cancelled) {
          callbacksRef.current.onLoadingChange?.(false);
        }
      }
    };

    renderWidget();

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  if (!siteKey || scriptError) {
    return null;
  }

  return <div ref={containerRef} />;
};

export default TurnstileWidget;
